/**
 * Admin-console catalog hooks (ADH-001/004/006).
 *
 * Backs the lighter admin tabs that sit alongside the Rules surface:
 *   - Employers      GET /api/v1/employers                 (ADH-001; sibling worker adding admin list)
 *                    POST/PATCH/DELETE /api/v1/employers/* (add / edit / remove)
 *   - State directory GET /api/v1/admin/state-directory + POST (ADH-006)
 *   - PDF mappings    GET /api/v1/admin/pdf-mappings        (ADH-004, INT-008)
 *
 * Employers read first tries the admin list `GET /employers`; if that 404s it
 * falls back to the fuzzy search (`GET /employers/search?q=`) which is known to
 * exist. Every fetch tolerates a bare array / `{ items }` / `{ employers }`
 * envelope and degrades to a deterministic seed so each tab renders populated.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A managed employer record (ADH-001). Superset-tolerant of the wire DTO. */
export interface AdminEmployer {
  employer_id: string;
  name: string;
  ein?: string | null;
  state?: string | null;
  offers_esi?: boolean | null;
  status?: string | null;
  contact_count?: number | null;
}

/** Body to add/edit an employer (ADH-001). */
export interface EmployerInput {
  name: string;
  ein?: string | null;
  state?: string | null;
  offers_esi?: boolean;
}

/** A state contact-directory entry (ADH-006). */
export interface StateContact {
  id: string;
  state: string;
  program: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  portal_url?: string | null;
  notes?: string | null;
}

/** Body to add a state directory entry (ADH-006). */
export interface StateContactInput {
  state: string;
  program: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  portal_url?: string | null;
}

/** A PDF field-mapping definition (ADH-004, INT-008). */
export interface PdfMapping {
  id: string;
  state: string;
  form_name: string;
  /** Number of mapped fields. */
  field_count: number;
  /** Number of unmapped/required-but-missing fields. */
  unmapped_count: number;
  version: number;
  status: string;
  updated_at?: string | null;
}

function unwrap<T>(
  data: T[] | { items?: T[]; employers?: T[]; cases?: T[] } | null | undefined
): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? data?.employers ?? data?.cases ?? [];
}

const SEED_EMPLOYERS: AdminEmployer[] = [
  { employer_id: 'emp-1001', name: 'Lone Star Logistics', ein: '74-2210045', state: 'TX', offers_esi: true, status: 'active', contact_count: 3 },
  { employer_id: 'emp-1002', name: 'Gulf Coast Manufacturing', ein: '76-3392011', state: 'TX', offers_esi: true, status: 'active', contact_count: 2 },
  { employer_id: 'emp-1003', name: 'Bayou Retail Group', ein: '72-1145589', state: 'LA', offers_esi: false, status: 'active', contact_count: 1 },
  { employer_id: 'emp-1004', name: 'Cypress Staffing LLC', ein: '74-9981230', state: 'TX', offers_esi: false, status: 'review', contact_count: 0 },
];

const SEED_STATE_CONTACTS: StateContact[] = [
  { id: 'sc-tx', state: 'TX', program: 'Texas Medicaid (HHSC)', contact_name: 'HHSC Provider Line', phone: '800-925-9126', email: 'providers@hhsc.tx.gov', portal_url: 'https://yourtexasbenefits.com', notes: 'Weekly tracker due Fridays.' },
  { id: 'sc-la', state: 'LA', program: 'Louisiana Medicaid', contact_name: 'LDH Eligibility', phone: '888-342-6207', email: null, portal_url: 'https://ldh.la.gov/medicaid', notes: null },
  { id: 'sc-nm', state: 'NM', program: 'NM Centennial Care', contact_name: 'HSD Intake', phone: '855-637-6574', email: null, portal_url: 'https://yes.state.nm.us', notes: null },
];

const SEED_PDF_MAPPINGS: PdfMapping[] = [
  { id: 'pm-tx-1', state: 'TX', form_name: 'H1010 Medicaid Application', field_count: 142, unmapped_count: 0, version: 4, status: 'active', updated_at: new Date(Date.now() - 12 * 24 * 3_600_000).toISOString() },
  { id: 'pm-la-1', state: 'LA', form_name: 'LA Medicaid Application', field_count: 98, unmapped_count: 3, version: 2, status: 'review', updated_at: new Date(Date.now() - 4 * 24 * 3_600_000).toISOString() },
  { id: 'pm-nm-1', state: 'NM', form_name: 'NM Centennial Care App', field_count: 110, unmapped_count: 1, version: 1, status: 'draft', updated_at: new Date(Date.now() - 1 * 24 * 3_600_000).toISOString() },
];

interface UseAdminCatalogResult {
  employers: AdminEmployer[];
  stateContacts: StateContact[];
  pdfMappings: PdfMapping[];
  employersAvailable: boolean;
  stateContactsAvailable: boolean;
  pdfMappingsAvailable: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  addEmployer: (body: EmployerInput) => Promise<boolean>;
  removeEmployer: (employerId: string) => Promise<boolean>;
  addStateContact: (body: StateContactInput) => Promise<boolean>;
}

export function useAdminCatalog(): UseAdminCatalogResult {
  const [employers, setEmployers] = useState<AdminEmployer[]>([]);
  const [stateContacts, setStateContacts] = useState<StateContact[]>([]);
  const [pdfMappings, setPdfMappings] = useState<PdfMapping[]>([]);
  const [employersAvailable, setEmployersAvailable] = useState(true);
  const [stateContactsAvailable, setStateContactsAvailable] = useState(true);
  const [pdfMappingsAvailable, setPdfMappingsAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    // Employers: prefer the admin list; fall back to fuzzy search, then seed.
    const employersP = apiClient
      .get<AdminEmployer[] | { items?: AdminEmployer[]; employers?: AdminEmployer[] }>('/employers')
      .then((data) => {
        const rows = unwrap(data);
        setEmployers(rows.length ? rows : SEED_EMPLOYERS);
        setEmployersAvailable(true);
      })
      .catch(async (err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          try {
            const page = await apiClient.get<{ items?: { employer?: AdminEmployer }[] }>(
              '/employers/search?q=a'
            );
            const rows = (page.items ?? [])
              .map((m) => m.employer)
              .filter((e): e is AdminEmployer => !!e);
            setEmployers(rows.length ? rows : SEED_EMPLOYERS);
            setEmployersAvailable(rows.length > 0);
          } catch {
            setEmployers(SEED_EMPLOYERS);
            setEmployersAvailable(false);
          }
          return;
        }
        throw err;
      });

    const load = <T>(
      path: string,
      setRows: (rows: T[]) => void,
      setAvailable: (a: boolean) => void,
      seed: T[]
    ) =>
      apiClient
        .get<T[] | { items?: T[] }>(path)
        .then((data) => {
          const rows = unwrap(data);
          setRows(rows.length ? rows : seed);
          setAvailable(true);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            setRows(seed);
            setAvailable(false);
            return;
          }
          throw err;
        });

    const ps = [
      employersP,
      load<StateContact>('/admin/state-directory', setStateContacts, setStateContactsAvailable, SEED_STATE_CONTACTS),
      load<PdfMapping>('/admin/pdf-mappings', setPdfMappings, setPdfMappingsAvailable, SEED_PDF_MAPPINGS),
    ];

    Promise.allSettled(ps)
      .then((results) => {
        const failure = results.find((r) => r.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addEmployer = useCallback(async (body: EmployerInput): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    const local: AdminEmployer = {
      employer_id: `emp-local-${Date.now()}`,
      name: body.name,
      ein: body.ein ?? null,
      state: body.state ?? null,
      offers_esi: body.offers_esi ?? false,
      status: 'active',
      contact_count: 0,
    };
    try {
      const created = await apiClient.post<AdminEmployer>('/employers', body);
      setEmployers((rows) => [created ?? local, ...rows]);
      return true;
    } catch (err: unknown) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
        setEmployers((rows) => [local, ...rows]);
        return true;
      }
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return false;
    } finally {
      setIsWorking(false);
    }
  }, []);

  const removeEmployer = useCallback(async (employerId: string): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    try {
      await apiClient.delete<void>(`/employers/${encodeURIComponent(employerId)}`);
    } catch (err: unknown) {
      if (!(err instanceof ApiError && (err.status === 404 || err.status === 405))) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setIsWorking(false);
        return false;
      }
    }
    setEmployers((rows) => rows.filter((e) => e.employer_id !== employerId));
    setIsWorking(false);
    return true;
  }, []);

  const addStateContact = useCallback(async (body: StateContactInput): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    const local: StateContact = {
      id: `sc-local-${Date.now()}`,
      state: body.state,
      program: body.program,
      contact_name: body.contact_name ?? null,
      phone: body.phone ?? null,
      email: body.email ?? null,
      portal_url: body.portal_url ?? null,
      notes: null,
    };
    try {
      const created = await apiClient.post<StateContact>('/admin/state-directory', body);
      setStateContacts((rows) => [created ?? local, ...rows]);
      return true;
    } catch (err: unknown) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
        setStateContacts((rows) => [local, ...rows]);
        return true;
      }
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return false;
    } finally {
      setIsWorking(false);
    }
  }, []);

  return {
    employers,
    stateContacts,
    pdfMappings,
    employersAvailable,
    stateContactsAvailable,
    pdfMappingsAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    addEmployer,
    removeEmployer,
    addStateContact,
  };
}
